import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import overflowIcon from '../common/icons/overflow.svg';

interface Props {
  onEdit?: () => void;
  onDelete?: () => void;
  onShare?: () => void;
  /** When set, the Share item is shown disabled with this label in place of
   *  "Share" (e.g. an admin-hidden listing that no share link can resolve). */
  shareDisabledLabel?: string;
  /** Replaces the default corner glyph where the trigger sits somewhere other
   *  than a card corner (e.g. the Activity row detail header). */
  icon?: React.ReactNode;
}

const MENU_WIDTH = 170;
// Flip the menu above the button when there is less than this much room below.
const FLIP_THRESHOLD = 170;

interface MenuPos {
  top: number;
  left: number;
  flip: boolean;
}

// Owner "..." menu. The dropdown is portaled to <body> with fixed positioning so
// it escapes the 120px card's overflow:hidden and the sibling-card stacking in a
// list — an in-card absolute popover would be clipped on both counts.
const ListingCardManageMenu: React.FC<Props> = ({
  onEdit,
  onDelete,
  onShare,
  shareDisabledLabel,
  icon,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<MenuPos | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;
    const r = buttonRef.current.getBoundingClientRect();
    const left = Math.max(
      8,
      Math.min(r.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8),
    );
    const flip = window.innerHeight - r.bottom < FLIP_THRESHOLD;
    setPos({ top: flip ? r.top - 6 : r.bottom + 6, left, flip });
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    const onDoc = (e: MouseEvent) => {
      const tgt = e.target as Node;
      if (buttonRef.current?.contains(tgt) || menuRef.current?.contains(tgt))
        return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  const pick = (fn?: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
    fn?.();
  };

  return (
    <div className="listing-card__manage" onClick={(e) => e.stopPropagation()}>
      <button
        ref={buttonRef}
        type="button"
        className="listing-card__manage-btn"
        aria-label={t('common.more', 'More')}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        {icon ?? (
          <img
            className="listing-card__manage-icon"
            src={overflowIcon}
            alt=""
            aria-hidden="true"
          />
        )}
      </button>
      {open &&
        pos &&
        ReactDOM.createPortal(
          <div
            ref={menuRef}
            className="listing-card__manage-menu"
            role="menu"
            style={{
              top: pos.top,
              left: pos.left,
              transform: pos.flip ? 'translateY(-100%)' : 'none',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {onEdit && (
              <button
                className="listing-card__manage-item"
                role="menuitem"
                onClick={pick(onEdit)}
              >
                {t('common.edit', 'Edit')}
              </button>
            )}
            {onShare && shareDisabledLabel && (
              <button
                className="listing-card__manage-item listing-card__manage-item--disabled"
                role="menuitem"
                aria-disabled="true"
                disabled
                title={shareDisabledLabel}
              >
                {shareDisabledLabel}
              </button>
            )}
            {onShare && !shareDisabledLabel && (
              <button
                className="listing-card__manage-item"
                role="menuitem"
                onClick={pick(onShare)}
              >
                {t('building.controls.share', 'Share')}
              </button>
            )}
            {onDelete && (
              <button
                className="listing-card__manage-item listing-card__manage-item--danger"
                role="menuitem"
                onClick={pick(onDelete)}
              >
                {t('common.remove', 'Remove')}
              </button>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default ListingCardManageMenu;
